export const heroBullets = [
  "Speech sound observation and documentation worksheets.",
  "Articulation activity planning resources.",
  "Visual practice materials and word organization templates.",
  "Session planning and progress tracking tools.",
  "Family communication and home practice organizers.",
  "Structured educational resources you can adapt to individual needs.",
];

export const beforeItems = [
  "You spend time searching for suitable speech sound activities.",
  "Your observation notes and session records are spread across different documents.",
  "You need a clearer way to organize speech sound information.",
  "Preparing visual materials and practice resources takes time.",
  "You want a more consistent way to document observations and review progress.",
  "You need practical tools that can be adapted to different children and therapy goals.",
];

export const afterItems = [
  "Organize relevant speech sound observations using dedicated worksheets.",
  "Find activity planning resources gathered in one collection.",
  "Prepare sessions using structured planning templates.",
  "Keep observation notes and follow-up records organized.",
  "Coordinate practice-related information with families when appropriate.",
  "Review documented observations to help inform your next professional decisions.",
];

export const problems = [
  {
    title: "You know what you want to work on, but planning the session takes too long.",
    body: "You have a general idea of the speech sound or skill you want to address, but finding suitable activities and organizing the session can take longer than expected.",
  },
  {
    title: "You need more organized speech sound materials.",
    body: "You want picture cards, word lists, activity examples, and worksheets that help you prepare educational practice materials without recreating everything from scratch.",
  },
  {
    title: "Your observations and session notes are scattered.",
    body: "Keeping relevant observations, activity records, and follow-up notes organized can become difficult when you use multiple documents.",
  },
  {
    title: "You want a clearer way to organize speech sound targets.",
    body: "You need a practical starting point for arranging speech sound information, identifying relevant questions, and documenting individualized planning decisions.",
  },
  {
    title: "You want to make home practice information easier to organize.",
    body: "When appropriate, you need a practical way to prepare family-friendly activity information and document recommendations made within your professional scope.",
  },
  {
    title: "You want to review progress more consistently.",
    body: "You want to organize observations over time and identify questions that may require adjustments to the plan or further assessment.",
  },
];

export const tools = [
  {
    name: "Speech Sound Observation Checklist",
    body: "A structured worksheet for organizing relevant observations and identifying questions that may need further exploration.",
    icon: "observation",
  },
  {
    name: "Articulation Session Planning Sheets",
    body: "Templates for organizing session priorities, activity ideas, and documented objectives.",
    icon: "session",
  },
  {
    name: "Speech Sound Word List Organizer",
    body: "A resource for arranging relevant word examples and practice materials according to your professional planning needs.",
    icon: "words",
  },
  {
    name: "Family Practice Information Sheets",
    body: "Templates for organizing appropriate activity information and communication with families.",
    icon: "family",
  },
  {
    name: "Progress Documentation Tracker",
    body: "A structured organizer for recording observations, session notes, follow-up questions, and planning decisions.",
    icon: "progress",
  },
] as const;

export const valueStack = {
  volumes: [
    { name: "Volume 1 — Speech Sound Foundations & Planning Framework", value: 19 },
    { name: "Volume 2 — Speech Sound Observation & Documentation Workbook", value: 19 },
    { name: "Volume 3 — Articulation Target Planning & Session Organizer", value: 19 },
    { name: "Volume 4 — Articulation Activities & Visual Practice Resources", value: 19 },
    { name: "Volume 5 — Family Communication & Home Practice Planner", value: 19 },
    { name: "Volume 6 — Speech Sound Progress Documentation & Follow-Up Planner", value: 19 },
  ],
  bonuses: [
    { name: "Bonus 1 — Speech Sound Picture Card Collection", value: 19 },
    { name: "Bonus 2 — Articulation Session Activity Planner", value: 17 },
    { name: "Bonus 3 — Speech Sound Word List Workbook", value: 17 },
    { name: "Bonus 4 — Family Home Practice Organizer", value: 19 },
    { name: "Bonus 5 — Speech Sound Documentation & Follow-Up Pack", value: 17 },
  ],
};

export const faqs = [
  {
    q: "What is the Pediatric Articulation Clinical Blueprint™?",
    a: [
      "It is a digital educational collection containing six practical volumes and five complementary resources focused on organizing speech sound observations, articulation activity planning, session preparation, family communication, and progress documentation.",
    ],
  },
  {
    q: "Who is this collection designed for?",
    a: [
      "It is primarily intended for speech-language pathologists and speech therapy professionals who work with children with speech sound difficulties.",
      "Students and other relevant professionals may also find some educational resources useful within their training and scope of practice.",
    ],
  },
  {
    q: "Is this a clinical certification program?",
    a: [
      "No. This is an educational resource collection, not a certification program, standardized assessment, or validated treatment protocol.",
    ],
  },
  {
    q: "Can I adapt the materials to different children?",
    a: [
      "Yes. The worksheets and planning templates are intended to be reviewed and adapted to individual communication needs, relevant observations, and professional decisions.",
      "They should not be used as a substitute for individualized assessment.",
    ],
  },
  {
    q: "How will I receive the materials?",
    a: ["The collection is delivered digitally, with access provided after your purchase is completed."],
  },
  {
    q: "Is this a recurring subscription?",
    a: ["No. The advertised price is a one-time payment of $27, with no monthly membership required."],
  },
  {
    q: "Is the collection available outside the United States?",
    a: [
      "The educational materials are designed for professional use, but terminology, speech sound examples, and practice words may need adaptation to the language and dialect relevant to your work. Currency conversion happens automatically at checkout.",
    ],
  },
  {
    q: "Is there a refund policy?",
    a: ["Please review the applicable purchase terms before completing your order."],
  },
];

export const buyers = [
  { name: "Valentina R.", country: "Colombia", minutes: 2 },
  { name: "Sofía M.", country: "Mexico", minutes: 4 },
  { name: "Camila G.", country: "Argentina", minutes: 1 },
  { name: "Lucía F.", country: "Spain", minutes: 6 },
  { name: "Mariana P.", country: "Chile", minutes: 3 },
  { name: "Daniela V.", country: "Peru", minutes: 5 },
  { name: "Gabriela T.", country: "Ecuador", minutes: 2 },
  { name: "Andrés L.", country: "Uruguay", minutes: 7 },
  { name: "Isabella C.", country: "Costa Rica", minutes: 3 },
  { name: "Paula S.", country: "Dominican Republic", minutes: 4 },
  { name: "Fernanda Q.", country: "Guatemala", minutes: 1 },
  { name: "Natalia B.", country: "Panama", minutes: 6 },
  { name: "Carolina D.", country: "Venezuela", minutes: 2 },
  { name: "Ximena A.", country: "Bolivia", minutes: 5 },
  { name: "Renata O.", country: "Paraguay", minutes: 3 },
  { name: "Alejandra N.", country: "Puerto Rico", minutes: 4 },
];
